import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Paper,
  Button,
  IconButton,
  Typography,
  TextField,
  CircularProgress,
  Chip,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  LinearProgress,
  Box,
} from "@material-ui/core";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";

import {
  RefreshCw as RefreshIcon,
  Plus as AddIcon,
  UploadCloud as UploadIcon,
  Trash2 as DeleteIcon,
  Eye as VisibilityIcon,
  RotateCcw as SyncIcon,
  FileSpreadsheet as SheetsIcon,
  LayoutGrid as RowsIcon,
  Database as DatabaseIcon,
  Layers as StorageIcon,
  Pencil as EditIcon,
  Check as CheckIcon,
  X as XIcon,
} from "lucide-react";
import api from "../../../services/api";
import toastError from "../../../errors/toastError";
import { toast } from "react-toastify";
import { useTheme } from "@material-ui/core/styles";
import ChatiaDialog from "../../../components/ChatiaDialog";
import { CancelButton } from "../../../components/ActionButtons";
import { i18n } from "../../../translate/i18n";

const hexToRgba = (hex, alpha = 1) => {
  if (!hex || typeof hex !== "string") return `rgba(25,118,210,${alpha})`;
  let h = hex.replace("#", "");
  if (h.length === 3) h = h.split("").map((c) => c + c).join("");
  const r = parseInt(h.substring(0, 2), 16);
  const g = parseInt(h.substring(2, 4), 16);
  const b = parseInt(h.substring(4, 6), 16);
  if ([r, g, b].some(Number.isNaN)) return `rgba(25,118,210,${alpha})`;
  return `rgba(${r},${g},${b},${alpha})`;
};

const StatCard = ({ title, value, icon, color, classes, isDark }) => (
  <Paper className={classes.statCard} variant="outlined" elevation={0}>
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
      <div>
        <Typography className={classes.statLabel}>{title}</Typography>
        <Typography className={classes.statNumber}>{value}</Typography>
      </div>
      <div 
        className={classes.statIcon}
        style={{ 
          background: `linear-gradient(135deg, ${color} 0%, ${hexToRgba(color, 0.7)} 100%)`,
          boxShadow: `0 6px 16px ${hexToRgba(color, 0.35)}`,
        }}
      >
        {React.cloneElement(icon, { size: 20 })}
      </div>
    </div>
    <div 
      style={{ 
        position: "absolute", 
        inset: 0, 
        background: `linear-gradient(135deg, ${hexToRgba(color, 0.05)} 0%, transparent 55%)`,
        pointerEvents: "none",
      }} 
    />
  </Paper>
);

const statusMap = {
  pending: { label: "Pendente", color: "default" },
  processing: { label: "Processando", color: "primary" },
  indexed: { label: "Indexado", color: "primary" },
  error: { label: "Erro", color: "secondary" },
  active: { label: "Ativo", color: "primary" },
  importing: { label: "Importando", color: "primary" },
};

const columnTypes = [
  { value: "text", label: "Texto", desc: "Texto livre" },
  { value: "number", label: "Número", desc: "Valores numéricos" },
  { value: "currency", label: "Moeda (R$)", desc: "Valores monetários" },
  { value: "date", label: "Data", desc: "Datas (AAAA-MM-DD)" },
  { value: "boolean", label: "Sim/Não", desc: "Verdadeiro ou falso" },
  { value: "url", label: "URL / Link", desc: "Links clicáveis" },
  { value: "image", label: "Imagem (URL)", desc: "URL de imagem com preview" },
  { value: "email", label: "E-mail", desc: "Endereços de email" },
  { value: "phone", label: "Telefone", desc: "Números de telefone" },
  { value: "select", label: "Lista de opções", desc: "Opções predefinidas" },
  { value: "textarea", label: "Texto longo", desc: "Textos grandes (resumos, descrições)" },
  { value: "json", label: "JSON", desc: "Dados complexos/estruturados" },
];

const slugify = (text) => {
  return text
    .toLowerCase()
    .normalize("NFD").replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9\s_-]/g, "")
    .trim()
    .replace(/[\s-]+/g, "_")
    .replace(/_+/g, "_")
    .replace(/^_|_$/g, "");
};

const CopyIcon = ({ size = 16 }) => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width={size} height={size} fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M16.9637 8.98209C16.9613 6.03194 16.9167 4.50384 16.0578 3.45753C15.892 3.25546 15.7067 3.07019 15.5047 2.90436C14.4008 1.99854 12.7609 1.99854 9.48087 1.99854C6.20089 1.99854 4.5609 1.99854 3.45708 2.90436C3.255 3.07018 3.06971 3.25546 2.90387 3.45753C1.99799 4.56128 1.99799 6.20116 1.99799 9.48091C1.99799 12.7607 1.99799 14.4005 2.90387 15.5043C3.0697 15.7063 3.255 15.8916 3.45708 16.0574C4.50346 16.9162 6.03167 16.9608 8.98201 16.9632" />
    <path d="M14.0283 9.02455L16.994 8.98193M14.0143 22.0013L16.9799 21.9586M21.9716 14.0221L21.9436 16.9818M9.01033 14.0357L8.98236 16.9953M11.4873 9.02455C10.6545 9.17371 9.31781 9.32713 9.01033 11.0488M19.4946 21.9586C20.3296 21.8223 21.6685 21.6894 22.0025 19.9726M19.4946 9.02455C20.3274 9.17371 21.6641 9.32713 21.9716 11.0488M11.5 21.9573C10.6672 21.8086 9.33039 21.6559 9.02197 19.9344" />
  </svg>
);

const SlugChip = ({ slug, isDark = false }) => {
  const [copied, setCopied] = useState(false);
  const handleCopy = (e) => {
    e.stopPropagation();
    try { navigator.clipboard.writeText(slug); } catch { }
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  if (!slug) return null;
  const bgColor = copied
    ? (isDark ? "rgba(46, 168, 90, 0.18)" : "#e8f5e9")
    : (isDark ? "rgba(255,255,255,0.06)" : "#f0f0f0");
  const textColor = copied
    ? (isDark ? "#81c784" : "#2e7d32")
    : (isDark ? "#d1d5db" : "#555");
  const borderColor = copied
    ? (isDark ? "rgba(129, 199, 132, 0.35)" : "rgba(46, 125, 50, 0.18)")
    : (isDark ? "rgba(255,255,255,0.10)" : "transparent");

  return (
    <Box display="inline-flex" alignItems="center" style={{
      backgroundColor: bgColor, borderRadius: 8, padding: "3px 9px",
      border: `1px solid ${borderColor}`,
      fontSize: 12, fontFamily: "monospace", color: textColor, gap: 4, cursor: "pointer",
      transition: "all 0.2s",
    }} onClick={handleCopy} title="Clique para copiar">
      <span>{slug}</span>
      {copied ? (
        <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="20 6 9 17 4 12" />
        </svg>
      ) : (
        <CopyIcon size={13} />
      )}
    </Box>
  );
};

const TypeChip = ({ type, isDark = false }) => {
  if (!type) return null;
  return (
    <Box display="inline-flex" alignItems="center" style={{
      backgroundColor: isDark ? "rgba(255,255,255,0.06)" : "#f0f0f0",
      borderRadius: 8,
      padding: "3px 9px",
      border: `1px solid ${isDark ? "rgba(255,255,255,0.10)" : "transparent"}`,
      fontSize: 12,
      color: isDark ? "#d1d5db" : "#555",
      lineHeight: 1.4,
    }}>
      {type}
    </Box>
  );
};

const cloneDataset = (dataset) => ({
  ...dataset,
  columns: (dataset?.columns || []).map((col) => ({
    ...col,
    options: Array.isArray(col.options) ? [...col.options] : col.options,
  })),
});

const DatasetsTab = ({ classes, purpose = "catalog" }) => {
  const theme = useTheme();
  const isDark = theme.palette.type === "dark" || theme.mode === "dark";
  const PRIMARY_MAIN = theme.palette.primary.main || "#1976d2";
  const t = (key, fallback, options) => {
    const fullKey = `knowledgeBase.datasets.${key}`;
    const value = i18n.t(fullKey, options);
    return !value || value === fullKey ? fallback : value;
  };

  const isCatalog = purpose === "catalog";
  const getModeLabel = (mode) => {
    const normalizedMode = mode || "ask";
    return t(`modes.${normalizedMode}`, normalizedMode);
  };

  const [datasets, setDatasets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [viewOpen, setViewOpen] = useState(false);
  const [selectedDS, setSelectedDS] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [editDraft, setEditDraft] = useState(null);
  const [savingEdit, setSavingEdit] = useState(false);
  const [editingColumnIndex, setEditingColumnIndex] = useState(null);
  const [rows, setRows] = useState([]);
  const [rowsTotal, setRowsTotal] = useState(0);
  const [rowsLoading, setRowsLoading] = useState(false);

  // Create form
  const [newName, setNewName] = useState("");
  const [newDescription, setNewDescription] = useState("");
  const [newColumns, setNewColumns] = useState([{ id: "", name: "", type: "text", mode: "ask", options: "" }]);

  // Import form
  const [importMode, setImportMode] = useState("google_sheets");
  const [sheetsUrl, setSheetsUrl] = useState("");
  const [importFile, setImportFile] = useState(null);
  const [apiKey, setApiKey] = useState("");
  const [importing, setImporting] = useState(false);

  // Add column form (inside view dialog)
  const [newColName, setNewColName] = useState("");
  const [newColType, setNewColType] = useState("text");
  const [newColOptions, setNewColOptions] = useState("");
  const [newColMode, setNewColMode] = useState("ask");

  const loadDatasets = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/datasets", { params: { purpose } });
      setDatasets(data);
    } catch (err) { toastError(err); }
    setLoading(false);
  }, [purpose]);

  useEffect(() => { loadDatasets(); }, [loadDatasets]);

  const handleCreate = async () => {
    if (!newName.trim()) return;
    const validCols = newColumns
      .filter(c => c.name.trim())
      .map(c => ({
        id: c.id,
        name: c.name.trim(),
        type: c.type,
        mode: c.mode || "ask",
        options: c.type === "select" && c.options ? c.options.split(",").map(o => o.trim()).filter(Boolean) : undefined,
      }));
    try {
      await api.post("/datasets", {
        name: newName,
        description: newDescription,
        columns: validCols,
        sourceType: "manual",
        purpose,
      });
      toast.success(t("toasts.created", "Dataset criado!"));
      setCreateOpen(false);
      setNewName("");
      setNewDescription("");
      setNewColumns([{ id: "", name: "", type: "text", mode: "ask", options: "" }]);
      loadDatasets();
    } catch (err) { toastError(err); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm(t("confirm.deleteDataset", "Excluir este dataset e todos os dados?"))) return;
    try {
      await api.delete(`/datasets/${id}`);
      toast.success(t("toasts.deleted", "Dataset excluído!"));
      loadDatasets();
    } catch (err) { toastError(err); }
  };

  const handleImport = async () => {
    if (!selectedDS) return;
    setImporting(true);
    try {
      if (importMode === "google_sheets" && sheetsUrl.trim()) {
        await api.post("/datasets/import-google-sheets", {
          datasetId: selectedDS.id,
          sheetsUrl: sheetsUrl.trim(),
        }, { timeout: 300000 });
      } else if (importMode === "csv" && importFile) {
        const formData = new FormData();
        formData.append("file", importFile);
        formData.append("datasetId", String(selectedDS.id));
        await api.post("/datasets/import-csv", formData, {
          headers: { "Content-Type": "multipart/form-data" },
          timeout: 300000,
        });
      } else {
        toast.error(t("toasts.fillImportSource", "Preencha a URL do Google Sheets ou selecione um arquivo CSV."));
        setImporting(false);
        return;
      }
      toast.success(t("toasts.imported", "Dados importados com sucesso!"));
      setImportOpen(false);
      setSheetsUrl("");
      setImportFile(null);
      loadDatasets();
    } catch (err) { toastError(err); }
    setImporting(false);
  };

  const handleResync = async (ds) => {
    if (!ds.sourceUrl) {
      toast.error(t("toasts.noSourceUrl", "Este dataset não tem URL de origem para ressincronizar."));
      return;
    }
    try {
      toast.info(t("toasts.resyncing", "Ressincronizando dados do Google Sheets..."));
      await api.post(`/datasets/${ds.id}/resync`, { apiKey: apiKey || undefined }, { timeout: 300000 });
      toast.success(t("toasts.resynced", "Dados ressincronizados!"));
      loadDatasets();
    } catch (err) { toastError(err); }
  };

  const openViewDialog = async (ds) => {
    const datasetCopy = cloneDataset(ds);
    setSelectedDS(datasetCopy);
    setEditDraft(datasetCopy);
    setEditMode(false);
    setEditingColumnIndex(null);
    setNewColName("");
    setNewColType("text");
    setViewOpen(true);
    if (isCatalog && ds.rowCount > 0) {
      setRowsLoading(true);
      try {
        const { data } = await api.get(`/datasets/${ds.id}/rows?limit=200`);
        setRows(data.rows || []);
        setRowsTotal(data.total || 0);
      } catch (err) { setRows([]); }
      setRowsLoading(false);
    } else if (isCatalog) {
      setRows([]);
      setRowsTotal(0);
    }
  };

  const closeViewDialog = () => {
    setViewOpen(false);
    setEditMode(false);
    setEditDraft(null);
    setEditingColumnIndex(null);
  };

  const handleStartEdit = (index) => {
    setEditDraft(cloneDataset(selectedDS));
    setEditMode(true);
    setEditingColumnIndex(index);
  };

  const handleCancelEdit = () => {
    setEditDraft(cloneDataset(selectedDS));
    setEditMode(false);
    setEditingColumnIndex(null);
  };

  const updateDraftColumn = (index, changes) => {
    setEditDraft((prev) => ({
      ...prev,
      columns: (prev?.columns || []).map((col, idx) => (
        idx === index ? { ...col, ...changes } : col
      )),
    }));
  };

  const handleSaveQualification = async () => {
    if (!selectedDS || !editDraft?.name?.trim()) return;

    const columns = (editDraft.columns || [])
      .filter((col) => col.name?.trim())
      .map((col) => ({
        ...col,
        id: col.id || slugify(col.name.trim()),
        name: col.name.trim(),
        type: col.type || "text",
        mode: col.mode || "ask",
        options: col.type === "select"
          ? (Array.isArray(col.options)
            ? col.options
            : String(col.options || "").split(",").map((option) => option.trim()).filter(Boolean))
          : undefined,
      }));

    setSavingEdit(true);
    try {
      const { data } = await api.put(`/datasets/${selectedDS.id}`, {
        name: editDraft.name.trim(),
        description: editDraft.description || "",
        columns,
      });
      const updatedDataset = cloneDataset(data);
      setSelectedDS(updatedDataset);
      setEditDraft(updatedDataset);
      setEditMode(false);
      setEditingColumnIndex(null);
      loadDatasets();
      toast.success(t("toasts.updated", "Qualificação atualizada!"));
    } catch (err) {
      toastError(err);
    } finally {
      setSavingEdit(false);
    }
  };

  const handleAddColumn = async () => {
    if (!newColName.trim() || !selectedDS) return;
    const col = { id: slugify(newColName.trim()), name: newColName.trim(), type: newColType, mode: newColMode || "ask" };
    if (newColType === "select" && newColOptions) {
      col.options = newColOptions.split(",").map(o => o.trim()).filter(Boolean);
    }
    if (editMode) {
      setEditDraft((prev) => ({ ...prev, columns: [...(prev?.columns || []), col] }));
      setNewColName("");
      setNewColType("text");
      setNewColOptions("");
      setNewColMode("ask");
      return;
    }
    const updatedCols = [...(selectedDS.columns || []), col];
    try {
      await api.put(`/datasets/${selectedDS.id}`, { columns: updatedCols });
      setSelectedDS(prev => ({ ...prev, columns: updatedCols }));
      setNewColName("");
      setNewColType("text");
      setNewColOptions("");
      setNewColMode("ask");
      loadDatasets();
      toast.success(t("toasts.columnAdded", "Coluna adicionada!"));
    } catch (err) { toastError(err); }
  };

  const handleDeleteColumn = async (colId) => {
    if (!selectedDS) return;
    const currentColumns = editMode ? (editDraft?.columns || []) : (selectedDS.columns || []);
    const col = currentColumns.find(c => (c.id || c.name) === colId);
    if (!window.confirm(t("confirm.removeColumn", `Remover a coluna "${col?.name || colId}"? Os dados dessa coluna serão perdidos.`, { column: col?.name || colId }))) return;
    const updatedCols = currentColumns.filter(c => (c.id || c.name) !== colId);
    if (editMode) {
      setEditDraft((prev) => ({ ...prev, columns: updatedCols }));
      return;
    }
    try {
      await api.put(`/datasets/${selectedDS.id}`, { columns: updatedCols });
      setSelectedDS(prev => ({ ...prev, columns: updatedCols }));
      loadDatasets();
    } catch (err) { toastError(err); }
  };

  const totalRows = datasets.reduce((acc, d) => acc + (d.rowCount || 0), 0);
  const googleSheetsCount = datasets.filter(d => d.sourceType === "google_sheets").length;

  const menuProps = {
    PaperProps: {
      className: classes.reportSelectMenu,
    },
    getContentAnchorEl: null,
    anchorOrigin: { vertical: "bottom", horizontal: "left" },
    transformOrigin: { vertical: "top", horizontal: "left" },
  };

  const typeMenuProps = {
    ...menuProps,
    PaperProps: {
      ...menuProps.PaperProps,
      style: {
        maxHeight: 220,
        overflowY: "auto",
      },
    },
    MenuListProps: {
      style: {
        paddingTop: 6,
        paddingBottom: 6,
      },
    },
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%", overflow: "hidden" }}>
      <div className={classes.pageHeader}>
        <div className={classes.headerLeft}>
          <span className={classes.pageIconWrap}>
            <StorageIcon size={20} strokeWidth={1.8} />
          </span>
          <div>
            <div className={classes.pageTitle}>
              {isCatalog ? t("titles.catalogs", "Catálogos") : t("titles.qualification", "Qualificação do Agente")}
              <span className={classes.countChip}>{datasets.length}</span>
            </div>
            <div className={classes.pageSubtitle}>
              {isCatalog
                ? t("subtitles.catalogs", "Dados estruturados para consulta de produtos, preços e disponibilidade")
                : t("subtitles.qualification", "Campos e datasets que o agente deve preencher durante o atendimento")}
            </div>
          </div>
        </div>

        <Box className={classes.headerActions}>
          <Button
            variant="outlined"
            size="small"
            onClick={loadDatasets}
            startIcon={<RefreshIcon size={16} />}
            style={{
              borderColor: PRIMARY_MAIN,
              color: PRIMARY_MAIN,
              borderRadius: 10,
              textTransform: "none",
              fontWeight: 600,
            }}
          >
            {t("actions.refresh", "Atualizar")}
          </Button>
          <Button
            variant="contained"
            color="primary"
            size="small"
            onClick={() => setCreateOpen(true)}
            startIcon={<AddIcon size={16} />}
            style={{ borderRadius: 10, textTransform: "none", fontWeight: 600 }}
          >
            {isCatalog ? t("actions.newCatalog", "Novo Catálogo") : t("actions.newQualification", "Nova Qualificação")}
          </Button>
        </Box>
      </div>

      <div className={classes.statsBox}>
        <StatCard title={t("stats.datasets", "Datasets")} value={datasets.length} icon={<DatabaseIcon />} color={PRIMARY_MAIN} classes={classes} isDark={isDark} />
        <StatCard title={t("stats.totalRows", "Linhas totais")} value={totalRows.toLocaleString()} icon={<RowsIcon />} color="#2EA85A" classes={classes} isDark={isDark} />
        <StatCard title="Google Sheets" value={googleSheetsCount} icon={<SheetsIcon />} color="#f59e0b" classes={classes} isDark={isDark} />
      </div>

      {loading && <LinearProgress style={{ margin: "0 16px 16px" }} />}

      <Paper className={classes.mainPaper} variant="outlined">
        <div className={classes.tableScroll}>
          {datasets.length === 0 && !loading ? (
            <div className={classes.emptyState}>
              <StorageIcon size={64} style={{ opacity: 0.3, marginBottom: 16 }} />
              <Typography variant="h6" gutterBottom>
                {isCatalog ? t("empty.catalogTitle", "Nenhum catálogo criado") : t("empty.qualificationTitle", "Nenhuma qualificação criada")}
              </Typography>
              <Typography variant="body2" gutterBottom>
                {isCatalog
                  ? t("empty.catalogDescription", "Crie um catálogo e importe dados (CSV, Google Sheets). O agente de IA usará esses dados para consultar produtos, preços e disponibilidade.")
                  : t("empty.qualificationDescription", "Crie os campos que o agente de IA deve preencher durante o atendimento (ex.: nome, interesse, temperatura do lead).")
                }
              </Typography>
              <Button 
                variant="contained" 
                color="primary" 
                onClick={() => setCreateOpen(true)} 
                startIcon={<AddIcon size={16} />} 
                style={{ marginTop: 16, borderRadius: 10 }}
              >
                {isCatalog ? t("actions.createFirstCatalog", "Criar primeiro catálogo") : t("actions.createFirstQualification", "Criar primeira qualificação")}
              </Button>
            </div>
          ) : (
            <Table stickyHeader className={classes.excelTable}>
              <TableHead>
                <TableRow>
                  <TableCell>{t("table.name", "Nome")}</TableCell>
                  <TableCell>{t("table.source", "Fonte")}</TableCell>
                  <TableCell>{t("table.columns", "Colunas")}</TableCell>
                  <TableCell>{t("table.rows", "Linhas")}</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>{t("table.createdAt", "Criado em")}</TableCell>
                  <TableCell align="right">{t("table.actions", "Ações")}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {datasets.map((ds) => {
                  const st = statusMap[ds.status] || statusMap.active;
                  return (
                    <TableRow key={ds.id} className={classes.zebraRow}>
                      <TableCell>
                        <Typography variant="subtitle2" style={{ fontWeight: 600, cursor: "pointer", color: PRIMARY_MAIN }} onClick={() => openViewDialog(ds)}>
                          {ds.name}
                        </Typography>
                        {ds.description && <Typography variant="caption" color="textSecondary">{ds.description}</Typography>}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={ds.sourceType === "google_sheets" ? "Google Sheets" : ds.sourceType?.toUpperCase()}
                          size="small"
                          variant="outlined"
                          style={ds.sourceType === "google_sheets" ? { borderColor: "#0f9d58", color: "#0f9d58", borderRadius: 6, fontSize: 11 } : { borderRadius: 6, fontSize: 11 }}
                        />
                      </TableCell>
                      <TableCell>{(ds.columns || []).length}</TableCell>
                      <TableCell>{ds.rowCount || 0}</TableCell>
                      <TableCell>
                        <Chip label={t(`status.${ds.status || "active"}`, st.label)} size="small" className={classes.statusChip}
                          style={ds.status === "active" ? { backgroundColor: "#2EA85A", color: "#fff" } : {}} />
                      </TableCell>
                      <TableCell>{new Date(ds.createdAt).toLocaleDateString("pt-BR")}</TableCell>
                      <TableCell align="right">
                        <Tooltip title={t("tooltips.viewData", "Ver dados")}>
                          <IconButton className={classes.iconActionBtn} onClick={() => openViewDialog(ds)}>
                            <VisibilityIcon size={18} />
                          </IconButton>
                        </Tooltip>
                        {isCatalog && (
                          <Tooltip title={t("tooltips.importData", "Importar dados")}>
                            <IconButton className={classes.iconActionBtn} onClick={() => { setSelectedDS(ds); setImportOpen(true); }}>
                              <UploadIcon size={18} />
                            </IconButton>
                          </Tooltip>
                        )}
                        {isCatalog && ds.sourceType === "google_sheets" && ds.sourceUrl && (
                          <Tooltip title={t("tooltips.resyncGoogleSheets", "Ressincronizar Google Sheets")}>
                            <IconButton className={classes.iconActionBtn} onClick={() => handleResync(ds)}>
                              <SyncIcon size={18} />
                            </IconButton>
                          </Tooltip>
                        )}
                        <Tooltip title={t("tooltips.delete", "Excluir")}>
                          <IconButton className={classes.deleteActionBtn} onClick={() => handleDelete(ds.id)}>
                            <DeleteIcon size={18} />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </div>
      </Paper>

      {/* Dialog: Criar */}
      <ChatiaDialog
        open={createOpen} 
        onClose={() => setCreateOpen(false)} 
        maxWidth="md" 
        title={isCatalog ? t("dialogs.newCatalogTitle", "Novo Catálogo") : t("dialogs.newQualificationTitle", "Nova Qualificação")}
        onSave={handleCreate}
        saveText={isCatalog ? t("actions.createCatalog", "Criar Catálogo") : t("actions.createQualification", "Criar Qualificação")}
        saveDisabled={!newName.trim()}
        footerChildren={<CancelButton onClick={() => setCreateOpen(false)}>{t("actions.cancel", "Cancelar")}</CancelButton>}
      >
          {isCatalog ? (
            <>
              <TextField label={t("form.catalogName", "Nome do catálogo")} value={newName} onChange={(e) => setNewName(e.target.value)} variant="outlined" margin="dense" fullWidth required InputProps={{ style: { borderRadius: 12, backgroundColor: isDark ? "rgba(255,255,255,0.02)" : "rgba(15,23,42,0.02)" } }} />
              <TextField label={t("form.description", "Descrição (opcional)")} value={newDescription} onChange={(e) => setNewDescription(e.target.value)} variant="outlined" margin="dense" fullWidth multiline minRows={2} InputProps={{ style: { borderRadius: 12, backgroundColor: isDark ? "rgba(255,255,255,0.02)" : "rgba(15,23,42,0.02)" } }} />
            </>
          ) : (
            <>
              <TextField label={t("form.name", "Nome")} value={newName} onChange={(e) => setNewName(e.target.value)} variant="outlined" margin="dense" fullWidth required InputProps={{ style: { borderRadius: 12, backgroundColor: isDark ? "rgba(255,255,255,0.02)" : "rgba(15,23,42,0.02)" } }} />
              <TextField label={t("form.description", "Descrição (opcional)")} value={newDescription} onChange={(e) => setNewDescription(e.target.value)} variant="outlined" margin="dense" fullWidth multiline minRows={2} InputProps={{ style: { borderRadius: 12, backgroundColor: isDark ? "rgba(255,255,255,0.02)" : "rgba(15,23,42,0.02)" } }} />
              {newColumns.map((col, idx) => (
                <Box key={idx} mt={2} mb={1.5}>
                  <Box
                    display="grid"
                    alignItems="center"
                    style={{
                      gridTemplateColumns: "minmax(0, 1fr) 230px 180px 40px",
                      columnGap: 20,
                    }}
                  >
                    <TextField label={t("form.fieldName", "Nome do campo")} value={col.name} onChange={(e) => {
                      const updated = [...newColumns];
                      updated[idx] = { ...updated[idx], name: e.target.value, id: slugify(e.target.value) };
                      setNewColumns(updated);
                    }} variant="outlined" size="small" fullWidth InputProps={{ style: { borderRadius: 10 } }} />
                    <FormControl variant="outlined" size="small" fullWidth>
                      <InputLabel>{t("form.type", "Tipo")}</InputLabel>
                      <Select 
                        className={`${classes.reportSelect} ${classes.compactSelect}`}
                        value={col.type} 
                        onChange={(e) => {
                          const updated = [...newColumns];
                          updated[idx] = { ...updated[idx], type: e.target.value };
                          setNewColumns(updated);
                        }} 
                        label={t("form.type", "Tipo")} 
                        style={{ borderRadius: 10 }}
                        MenuProps={typeMenuProps}
                      >
                        {columnTypes.map((type) => (
                          <MenuItem key={type.value} value={type.value} className={classes.reportSelectMenuItem}>{t(`columnTypes.${type.value}`, type.label)}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <FormControl variant="outlined" size="small" fullWidth>
                      <InputLabel>{t("table.mode", "Modo")}</InputLabel>
                      <Select
                        className={`${classes.reportSelect} ${classes.compactSelect}`}
                        value={col.mode || "ask"}
                        onChange={(e) => {
                          const updated = [...newColumns];
                          updated[idx] = { ...updated[idx], mode: e.target.value };
                          setNewColumns(updated);
                        }}
                        label={t("table.mode", "Modo")}
                        style={{ borderRadius: 10 }}
                        MenuProps={menuProps}
                      >
                        <MenuItem value="ask" className={classes.reportSelectMenuItem}>{t("modes.ask", "Pergunta")}</MenuItem>
                        <MenuItem value="internal" className={classes.reportSelectMenuItem}>{t("modes.internal", "Interno")}</MenuItem>
                      </Select>
                    </FormControl>
                    <IconButton size="small" onClick={() => setNewColumns(prev => prev.filter((_, i) => i !== idx))} disabled={newColumns.length <= 1} style={{ borderRadius: 10, justifySelf: "center" }}>
                      <DeleteIcon size={18} />
                    </IconButton>
                  </Box>
                  {col.id && <div style={{ marginTop: 8 }}><SlugChip slug={col.id} isDark={isDark} /></div>}
                </Box>
              ))}
              <Button size="small" startIcon={<AddIcon size={16} />} onClick={() => setNewColumns(prev => [...prev, { id: "", name: "", type: "text", mode: "ask", options: "" }])} style={{ textTransform: "none", fontWeight: 600, color: PRIMARY_MAIN }}>{t("actions.addField", "Adicionar campo")}</Button>
            </>
          )}
      </ChatiaDialog>

      <ChatiaDialog
        open={importOpen} 
        onClose={() => !importing && setImportOpen(false)} 
        maxWidth="md" 
        title={t("dialogs.importTitle", "Importar Dados - {{name}}", { name: selectedDS?.name || "" })}
        onSave={handleImport}
        saveText={t("actions.importData", "Importar Dados")}
        saveDisabled={importing}
        saveLoading={importing}
        footerChildren={<CancelButton onClick={() => setImportOpen(false)} disabled={importing}>{t("actions.cancel", "Cancelar")}</CancelButton>}
      >
          <Box mt={1} mb={3} display="flex" style={{ gap: 12 }}>
            <Button variant={importMode === "google_sheets" ? "contained" : "outlined"} color={importMode === "google_sheets" ? "primary" : "default"} size="small" onClick={() => setImportMode("google_sheets")} style={{ borderRadius: 10, textTransform: "none", fontWeight: 600, padding: "8px 20px" }}>Google Sheets</Button>
            <Button variant={importMode === "csv" ? "contained" : "outlined"} color={importMode === "csv" ? "primary" : "default"} size="small" onClick={() => setImportMode("csv")} startIcon={<UploadIcon size={16} />} style={{ borderRadius: 10, textTransform: "none", fontWeight: 600, padding: "8px 20px" }}>{t("importModes.csv", "Arquivo CSV")}</Button>
          </Box>
          {importMode === "google_sheets" && <TextField label={t("form.googleSheetsLink", "Link do Google Sheets")} value={sheetsUrl} onChange={(e) => setSheetsUrl(e.target.value)} variant="outlined" margin="dense" fullWidth InputProps={{ style: { borderRadius: 12, backgroundColor: isDark ? "rgba(255,255,255,0.02)" : "rgba(15,23,42,0.02)" } }} />}
          {importMode === "csv" && (
            <Box p={3} style={{ border: `2px dashed ${isDark ? "rgba(255,255,255,0.1)" : "rgba(15,23,42,0.1)"}`, borderRadius: 12, textAlign: "center", backgroundColor: isDark ? "rgba(255,255,255,0.02)" : "rgba(15,23,42,0.02)" }}>
              <input type="file" accept=".csv" onChange={(e) => setImportFile(e.target.files[0] || null)} id="csv-upload" style={{ display: "none" }} />
              <label htmlFor="csv-upload">
                <Button component="span" startIcon={<UploadIcon size={20} />} style={{ textTransform: "none", fontWeight: 600 }}>
                  {importFile ? importFile.name : t("form.csvPlaceholder", "Clique para selecionar arquivo CSV")}
                </Button>
              </label>
            </Box>
          )}
          {importing && <Box display="flex" alignItems="center" justifyContent="center" mt={3} p={2} style={{ background: isDark ? "rgba(255,255,255,0.04)" : "rgba(15,23,42,0.04)", borderRadius: 12 }}><CircularProgress size={24} style={{ marginRight: 16 }} /><Typography style={{ fontWeight: 600 }}>{t("dialogs.processingImport", "Processando importação...")}</Typography></Box>}
      </ChatiaDialog>

      <ChatiaDialog
        open={viewOpen} 
        onClose={closeViewDialog} 
        maxWidth={isCatalog ? "xl" : "md"} 
        title={selectedDS?.name || ""}
        hideFooter
      >
          {isCatalog ? (
            rowsLoading ? <Box display="flex" justifyContent="center" p={4}><CircularProgress /></Box> : (
              <div className={classes.tableScroll} style={{ maxHeight: 500, borderRadius: 12, border: `1px solid ${isDark ? "rgba(255,255,255,0.08)" : "rgba(15,23,42,0.08)"}` }}>
                <Table stickyHeader className={classes.excelTable}>
                  <TableHead>
                    <TableRow>
                    <TableCell style={{ background: isDark ? "#1a1f29" : "#f9fafb", fontWeight: 700 }}>#</TableCell>
                      {(selectedDS?.columns || []).map(col => <TableCell key={col.id || col.name} style={{ background: isDark ? "#1a1f29" : "#f9fafb", fontWeight: 700 }}>{col.name}</TableCell>)}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {rows.map((row, ri) => (
                      <TableRow key={row.id || ri} className={classes.zebraRow}>
                        <TableCell>{ri + 1}</TableCell>
                        {(selectedDS?.columns || []).map(col => <TableCell key={col.id || col.name}>{row.data?.[col.id || col.name] ?? ""}</TableCell>)}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )
          ) : (
            <>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2} style={{ gap: 12 }}>
                <Typography variant="body2" color="textSecondary">
                  {(selectedDS?.columns || []).length} {t("table.columns", "Colunas").toLowerCase()}
                </Typography>
              </Box>

              <div className={classes.tableScroll} style={{ maxHeight: 500, borderRadius: 12, border: `1px solid ${isDark ? "rgba(255,255,255,0.08)" : "rgba(15,23,42,0.08)"}` }}>
                <Table stickyHeader className={classes.excelTable}>
                  <TableHead>
                    <TableRow>
                      <TableCell>{t("table.name", "Nome")}</TableCell>
                      <TableCell>Slug</TableCell>
                      <TableCell>{t("table.type", "Tipo")}</TableCell>
                      <TableCell>{t("table.mode", "Modo")}</TableCell>
                      <TableCell align="right"></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {((editMode ? editDraft?.columns : selectedDS?.columns) || []).map((col, idx) => {
                      const isEditingRow = editMode && editingColumnIndex === idx;

                      return (
                        <TableRow key={col.id || idx} className={classes.zebraRow}>
                          <TableCell style={{ fontWeight: 600 }}>
                            {isEditingRow ? (
                              <TextField
                                value={col.name || ""}
                                onChange={(e) => updateDraftColumn(idx, { name: e.target.value })}
                                variant="outlined"
                                size="small"
                                fullWidth
                                InputProps={{ style: { borderRadius: 10 } }}
                              />
                            ) : col.name}
                          </TableCell>
                          <TableCell><SlugChip slug={col.id || slugify(col.name || "")} isDark={isDark} /></TableCell>
                          <TableCell>
                            {isEditingRow ? (
                              <>
                                <FormControl variant="outlined" size="small" fullWidth>
                                  <Select
                                    className={`${classes.reportSelect} ${classes.compactSelect}`}
                                    value={col.type || "text"}
                                    onChange={(e) => updateDraftColumn(idx, { type: e.target.value })}
                                    style={{ borderRadius: 10 }}
                                    MenuProps={typeMenuProps}
                                  >
                                    {columnTypes.map((type) => <MenuItem key={type.value} value={type.value} className={classes.reportSelectMenuItem}>{t(`columnTypes.${type.value}`, type.label)}</MenuItem>)}
                                  </Select>
                                </FormControl>
                                {(col.type || "text") === "select" && (
                                  <TextField
                                    value={Array.isArray(col.options) ? col.options.join(", ") : (col.options || "")}
                                    onChange={(e) => updateDraftColumn(idx, { options: e.target.value })}
                                    variant="outlined"
                                    size="small"
                                    fullWidth
                                    placeholder={t("form.optionsPlaceholder", "Opções separadas por vírgula")}
                                    style={{ marginTop: 8 }}
                                    InputProps={{ style: { borderRadius: 10 } }}
                                  />
                                )}
                              </>
                            ) : (
                              <TypeChip type={col.type} isDark={isDark} />
                            )}
                          </TableCell>
                          <TableCell>
                            {isEditingRow ? (
                              <FormControl variant="outlined" size="small" fullWidth>
                                <Select
                                  className={`${classes.reportSelect} ${classes.compactSelect}`}
                                  value={col.mode || "ask"}
                                  onChange={(e) => updateDraftColumn(idx, { mode: e.target.value })}
                                  style={{ borderRadius: 10 }}
                                  MenuProps={menuProps}
                                >
                                  <MenuItem value="ask" className={classes.reportSelectMenuItem}>{t("modes.ask", "Pergunta")}</MenuItem>
                                  <MenuItem value="internal" className={classes.reportSelectMenuItem}>{t("modes.internal", "Interno")}</MenuItem>
                                </Select>
                              </FormControl>
                            ) : getModeLabel(col.mode)}
                          </TableCell>
                          <TableCell align="right">
                            {isEditingRow ? (
                              <>
                                <Tooltip title={t("actions.save", "Salvar")}>
                                  <IconButton className={classes.iconActionBtn} onClick={handleSaveQualification} disabled={savingEdit || !col.name?.trim()}>
                                    {savingEdit ? <CircularProgress size={18} /> : <CheckIcon size={18} />}
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title={t("actions.cancel", "Cancelar")}>
                                  <IconButton className={classes.iconActionBtn} onClick={handleCancelEdit} disabled={savingEdit}>
                                    <XIcon size={18} />
                                  </IconButton>
                                </Tooltip>
                              </>
                            ) : (
                              <>
                                <Tooltip title={t("actions.edit", "Editar")}>
                                  <IconButton className={classes.iconActionBtn} onClick={() => handleStartEdit(idx)} disabled={editMode || savingEdit}>
                                    <EditIcon size={18} />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title={t("tooltips.delete", "Excluir")}>
                                  <IconButton className={classes.deleteActionBtn} onClick={() => handleDeleteColumn(col.id || col.name)} disabled={editMode || savingEdit}>
                                    <DeleteIcon size={18} />
                                  </IconButton>
                                </Tooltip>
                              </>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                    <TableRow>
                      <TableCell><TextField value={newColName} onChange={(e) => setNewColName(e.target.value)} variant="outlined" size="small" fullWidth placeholder={t("form.newFieldPlaceholder", "Novo campo...")} InputProps={{ style: { borderRadius: 10 } }} /></TableCell>
                      <TableCell>{newColName && <SlugChip slug={slugify(newColName)} isDark={isDark} />}</TableCell>
                      <TableCell>
                        <FormControl variant="outlined" size="small" fullWidth>
                          <Select
                            className={`${classes.reportSelect} ${classes.compactSelect}`}
                            value={newColType}
                            onChange={(e) => setNewColType(e.target.value)}
                            style={{ borderRadius: 10 }}
                            MenuProps={typeMenuProps}
                          >
                            {columnTypes.map((type) => <MenuItem key={type.value} value={type.value} className={classes.reportSelectMenuItem}>{t(`columnTypes.${type.value}`, type.label)}</MenuItem>)}
                          </Select>
                        </FormControl>
                      </TableCell>
                      <TableCell>
                        <FormControl variant="outlined" size="small" fullWidth>
                          <Select
                            className={`${classes.reportSelect} ${classes.compactSelect}`}
                            value={newColMode}
                            onChange={(e) => setNewColMode(e.target.value)}
                            style={{ borderRadius: 10 }}
                            MenuProps={menuProps}
                          >
                            <MenuItem value="ask" className={classes.reportSelectMenuItem}>{t("modes.ask", "Pergunta")}</MenuItem>
                            <MenuItem value="internal" className={classes.reportSelectMenuItem}>{t("modes.internal", "Interno")}</MenuItem>
                          </Select>
                        </FormControl>
                      </TableCell>
                      <TableCell align="right"><IconButton size="small" color="primary" onClick={handleAddColumn} disabled={!newColName.trim() || editMode || savingEdit} style={{ borderRadius: 10 }}><AddIcon size={18} /></IconButton></TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </div>
            </>
          )}
      </ChatiaDialog>
    </div>
  );
};

export default DatasetsTab;
